import React, { useEffect, useMemo, useState } from 'react';
import BrandStatus from 'components/brand-status';
import {
  FileUploader,
  Input,
  Multiselect,
  Switch,
  Textarea,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import useBrandContext from 'contexts/brand.context';
import { SuccessModal } from 'components/base/success-modal';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import SingleBottomNav from 'components/base/single-bottom-nav';
import { isEqual } from 'lodash';
import {
  PROVIDER_BRAND_TYPE_OPTIONS,
  FCB_BRAND_TYPE_OPTIONS,
  STORAGE_KEYS,
  FC_BRAND_TYPE_OPTIONS
} from 'utils/constants';
import LiveTag from 'components/live-tag';
import { ENUM_BRAND_TYPE } from 'utils/enum';
import { generateSlug, handleApiError, isFCBBrand, isFCBCustomBrand } from 'utils/functions';
import TimeSelector from 'components/base/time-selector';

const EditBrandDetails = () => {
  const {
    createBrandForm,
    setCreateBrandForm,
    activeBrand,
    companyOptions,
    loadingCompanyOptions
  } = useBrandContext();

  const isFoodCourt = useMemo(() => {
    const isInOptions = FC_BRAND_TYPE_OPTIONS.some(
      (opt) => opt.value === createBrandForm.brand_type
    );

    const isOthers =
      createBrandForm.preorder ||
      createBrandForm.is_private ||
      isFCBBrand(createBrandForm.brand_type);

    return isInOptions && !isOthers;
  }, [createBrandForm.brand_type, createBrandForm.preorder, createBrandForm.is_private]);

  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;

  const changeIsFCB = (checked: boolean) => {
    if (checked)
      setCreateBrandForm({
        ...createBrandForm,
        is_private: false,
        brand_type: isFCBCustomBrand(activeBrand.brand_type)
          ? activeBrand.brand_type
          : ENUM_BRAND_TYPE.FOODCOURT_BUSINESS
      });
    else
      setCreateBrandForm({
        ...createBrandForm,
        brand_type: ENUM_BRAND_TYPE.FOODCOURT,
        is_private: false
      });
  };

  useEffect(() => {
    const payload: any = {};
    Object.keys(createBrandForm).forEach((key: string) => {
      if (createBrandForm[key] === undefined || createBrandForm[key] === '') return;
      if (!isEqual(createBrandForm[key], activeBrand[key])) {
        payload[key] = createBrandForm[key];
      }
    });
    if (Object?.keys(payload).length > 0) {
      setIsDisabled(false);
    }
  }, [createBrandForm]);

  const updateBrandDetails = async () => {
    setLoading(true);
    const payload: Record<string, unknown> = { brand_id: activeBrand?.id };
    const formData: Record<string, unknown> = {
      ...createBrandForm,
      cutoff_time: createBrandForm.cutoff_time
        ? `${createBrandForm.cutoff_time?.timeWithColon}:00Z`
        : null,
      company_ids:
        !createBrandForm.all_company_available && createBrandForm.company_ids.length
          ? createBrandForm.company_ids
          : undefined
    };
    Object.keys(formData).forEach((key: string) => {
      if (formData[key] === undefined || formData[key] === '') return;
      if (!isEqual(formData[key], activeBrand[key])) {
        payload[key] = formData[key];
      }
    });
    try {
      const res = await apiService.put('marketing/brand', {
        data: payload
      });
      if (res?.data) {
        setShowSuccess(true);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const switchToLive = async (e: boolean) => {
    try {
      setCreateBrandForm({ ...createBrandForm, live: e });
      const payload = {
        brand_id: activeBrand?.id,
        live: e
      };
      const res = await apiService.post('marketing/brand-go-live', {
        data: payload
      });
      if (res.status === 201) {
        toast(<ToastMessage type='success' message={`Brand is ${e ? 'now live' : 'offline'} `} />);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    }
  };

  const brandSlug = generateSlug(createBrandForm.name ?? '');
  return (
    <div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <Input
          type='text'
          label='Brand Name'
          value={createBrandForm?.name}
          onChange={(e) => {
            const name = e.target.value;
            setCreateBrandForm({
              ...createBrandForm,
              name,
              slug: brandSlug
            });
          }}
        />
      </div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <Input
          type='text'
          label='Brand Slug'
          placeholder='Enter brand slug'
          value={createBrandForm?.slug || ''}
          onChange={(e) =>
            setCreateBrandForm({
              ...createBrandForm,
              slug: e.target.value
            })
          }
        />
        {createBrandForm?.slug && (
          <p className='text-gray-5 mt-1'>
            Preview: https://getfoodcourt.com/restaurants/{createBrandForm?.slug}
          </p>
        )}
      </div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <Input
          type='text'
          label='Brand Tagline'
          value={createBrandForm?.short_text}
          onChange={(e) => setCreateBrandForm({ ...createBrandForm, short_text: e.target.value })}
          maxLength={15}
        />
        <div className='bg-[#FEF7E1] rounded-md p-[10px] flex items-center gap-[8px] text-[#BD8F05] mt-4'>
          <InfoIcon />
          <p className='text-xs text-[#85806F]'>Enter a catchy & simple tagline for the brand</p>
        </div>
      </div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <Textarea
          label='Brand Summary'
          value={createBrandForm?.summary}
          onChange={(e) => setCreateBrandForm({ ...createBrandForm, summary: e.target.value })}
          rows={3}
        />
      </div>
      <div className='border border-[#EDEDEE] rounded-xl px-6 py-4 mb-6'>
        <Textarea
          label='Brand Description'
          value={createBrandForm?.description}
          onChange={(e) => setCreateBrandForm({ ...createBrandForm, description: e.target.value })}
          rows={4}
        />
      </div>
      <div className='flex gap-4'>
        <Input
          type='radio'
          name='brandType'
          checked={createBrandForm?.preorder}
          onChange={(e) => {
            setCreateBrandForm({
              ...createBrandForm,
              is_private: false,
              preorder: e.target.checked
            });
          }}
          label='Pre order'
          className='mt-3 ml-2'
        />
        <Input
          type='radio'
          name='brandType'
          checked={createBrandForm?.is_private}
          onChange={(e) => {
            setCreateBrandForm({
              ...createBrandForm,
              preorder: false,
              is_private: e.target.checked,
              brand_type: ENUM_BRAND_TYPE.FOODCOURT
            });
          }}
          label='Private'
          className='mt-3 ml-2'
        />
        <Input
          type='radio'
          name='brandType'
          checked={isFCBBrand(createBrandForm.brand_type)}
          onChange={(e) => {
            changeIsFCB(e.target.checked);
          }}
          label='FCB'
          className='mt-3 ml-2'
        />
        <Input
          type='radio'
          name='brandType'
          checked={isFoodCourt}
          onChange={() => {
            setCreateBrandForm({
              ...createBrandForm,
              preorder: false,
              is_private: false,
              brand_type: activeBrand.brand_type
            });
          }}
          label='Foodcourt'
          className='mt-3 ml-2'
        />
      </div>

      {isFoodCourt && (
        <div className='flex flex-col gap-1 my-6'>
          <p className='font-medium'>Brand Type</p>
          <Multiselect
            isSingle
            placeholder='Select brand type'
            values={[createBrandForm.brand_type]}
            onChange={(values) => {
              setCreateBrandForm({
                ...createBrandForm,
                brand_type: values[0] as ENUM_BRAND_TYPE
              });
            }}
            options={FC_BRAND_TYPE_OPTIONS}
          />
        </div>
      )}
      {createBrandForm?.preorder && (
        <Input
          type='number'
          small
          min={0}
          max={100}
          label='Pre order duration in hours'
          value={createBrandForm?.preorder_duration_in_hours}
          onChange={(e) =>
            setCreateBrandForm({
              ...createBrandForm,
              preorder_duration_in_hours: parseInt(e.target.value)
            })
          }
          className='mt-5'
        />
      )}
      {createBrandForm?.is_private && (
        <div className='flex flex-col gap-1 my-6'>
          <p className='font-medium'>Brand Type</p>
          <div className='cursor-not-allowed'>
            <Multiselect
              disabled
              isSingle
              placeholder='Select brand type'
              values={[createBrandForm.brand_type]}
              onChange={(values) =>
                setCreateBrandForm({
                  ...createBrandForm,
                  brand_type: values[0] as ENUM_BRAND_TYPE
                })
              }
              options={PROVIDER_BRAND_TYPE_OPTIONS}
            />
          </div>
          {createBrandForm.brand_type === ENUM_BRAND_TYPE.GLOVO && (
            <Input
              className='my-4'
              type='text'
              small
              label='Store ID'
              value={createBrandForm.brand_partnership_id}
              onChange={(e: any) =>
                setCreateBrandForm({
                  ...createBrandForm,
                  brand_partnership_id: e.target.value
                })
              }
            />
          )}
        </div>
      )}
      {isFCBBrand(createBrandForm.brand_type) ? (
        <div className='flex flex-col gap-6 border border-[#EDEDEE] rounded-xl px-6 py-4 my-6'>
          <div className='flex flex-col gap-1'>
            <p className='font-medium'>Brand Type</p>
            <Multiselect
              isSingle
              placeholder='Select brand type'
              values={
                createBrandForm?.preorder || isFCBCustomBrand(createBrandForm.brand_type)
                  ? [createBrandForm.brand_type]
                  : []
              }
              onChange={(values) =>
                setCreateBrandForm({ ...createBrandForm, brand_type: values[0] as ENUM_BRAND_TYPE })
              }
              options={
                createBrandForm?.preorder
                  ? [{ value: ENUM_BRAND_TYPE.FOODCOURT_BUSINESS, text: 'FoodCourt Business' }]
                  : FCB_BRAND_TYPE_OPTIONS
              }
            />
          </div>
          {!createBrandForm?.preorder && (
            <div className='flex flex-col gap-3'>
              <p className='font-medium'>Cut off time</p>
              <TimeSelector
                value={createBrandForm.cutoff_time || {}}
                onChange={(cutoff_time) => setCreateBrandForm({ ...createBrandForm, cutoff_time })}
              />
            </div>
          )}

          <div className='flex flex-col gap-3 my-3'>
            <p className='font-medium'>Companies</p>
            <div className='flex items-center gap-6'>
              <Input
                type='checkbox'
                label='Available to all companies'
                checked={createBrandForm.all_company_available}
                onChange={() =>
                  setCreateBrandForm({
                    ...createBrandForm,
                    all_company_available: true
                  })
                }
              />
              <Input
                type='checkbox'
                label='Available to selected companies'
                checked={!createBrandForm.all_company_available}
                onChange={() =>
                  setCreateBrandForm({
                    ...createBrandForm,
                    all_company_available: false
                  })
                }
              />
            </div>
            {!createBrandForm.all_company_available && (
              <Multiselect
                hasSearch
                filterResults
                placeholder='Select companies'
                loading={loadingCompanyOptions}
                values={createBrandForm.company_ids}
                onChange={(company_ids) => setCreateBrandForm({ ...createBrandForm, company_ids })}
                options={companyOptions}
              />
            )}
          </div>
        </div>
      ) : null}
      <div className='my-4'>
        <BrandStatus
          isTop={createBrandForm?.top}
          isNew={createBrandForm?.new}
          popup={createBrandForm?.pop_up}
          comingSoon={createBrandForm?.coming_soon}
          comment={createBrandForm?.coming_soon_text}
          color={createBrandForm?.popup_color}
          setComingSoon={(coming_soon) => setCreateBrandForm({ ...createBrandForm, coming_soon })}
          setNew={(k) => setCreateBrandForm({ ...createBrandForm, new: k })}
          setPopup={(pop_up) => setCreateBrandForm({ ...createBrandForm, pop_up })}
          setComment={(coming_soon_text) =>
            setCreateBrandForm({ ...createBrandForm, coming_soon_text })
          }
          setColor={(popup_color) => setCreateBrandForm({ ...createBrandForm, popup_color })}
          setIsTop={(value) => setCreateBrandForm({ ...createBrandForm, top: value })}
          className='my-6'
        />
      </div>
      <div className='border py-[20px] px-[24px] border-[#EDEDEE] rounded-xl flex justify-between my-8'>
        <div className='flex items-center justify-start gap-7'>
          <p className='text-xs font-medium'>Brand Active</p>
          <LiveTag
            live={createBrandForm.live}
            brandType={createBrandForm?.brand_type}
            partnerSynced={activeBrand.partner_synced}
          />
        </div>
        <Switch
          active={createBrandForm?.live}
          onChange={(e) => switchToLive(e)}
          label='Roll Back'
        />
      </div>
      {createBrandForm?.logo && (
        <div className='rounded-xl border border-[#EDEDEE] pt-[18px]'>
          <div className='border-b border-b-[#EDEDEE] pb-2 px-6'>
            <p className='text-sm font-medium text-[#858585]'>Brand Logo & Image</p>
          </div>
          <div className='p-6 flex gap-[20px]'>
            <FileUploader
              className='basis-[260px] shrink-0 bg-[#F4F5F5] p-1'
              token={token}
              setFileUrls={(urls) => {
                setCreateBrandForm({
                  ...createBrandForm,
                  logo: urls?.[0]
                });
              }}
              fileUrls={createBrandForm.logo ? [createBrandForm?.logo] : []}
            />
            <FileUploader
              token={token}
              setFileUrls={(urls) => {
                setCreateBrandForm({
                  ...createBrandForm,
                  images: urls
                });
              }}
              fileUrls={createBrandForm?.images}
            />
          </div>
        </div>
      )}
      <SingleBottomNav
        loading={loading}
        onConfirm={updateBrandDetails}
        backBtndisabled
        btnDisabled={isDisabled || loading}
      />
      <SuccessModal onClose={() => setShowSuccess(false)} show={showSuccess}>
        Brand updated successfully
      </SuccessModal>
    </div>
  );
};

export default EditBrandDetails;
